import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import type { SxProps, Theme } from '@mui/material/styles'
import { Fragment, useCallback, useId, useMemo, useRef, useState } from 'react'

import { colorTokens } from '../tokens/colors.ts'
import KestrixV2Typography from '../typography/KestrixTypography.tsx'
import type {
  KestrixChartLegendItem,
  KestrixChartLegendProps
} from './types.ts'

const HOVER_LEAVE_DELAY_MS = 120
const DEFAULT_MAX_VISIBLE = 8

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  rowGap: '8px',
  columnGap: '16px',
  width: '100%',
  minWidth: 0
}

const rowSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0
}

const swatchSx = (color: string): SxProps<Theme> => ({
  flexShrink: 0,
  width: '12px',
  height: '12px',
  borderRadius: '2px',
  background: color,
  boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.04)'
})

const labelSx: SxProps<Theme> = {
  color: colorTokens.text.bodyNeutral,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}

const indicatorSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: '12px',
  border: `1px solid ${colorTokens.global.neutral[100]}`,
  backgroundColor: colorTokens.semantic.brand.base.white,
  color: colorTokens.text.subtextNeutral,
  cursor: 'default',
  outline: 'none',
  transition: 'background-color 120ms ease, border-color 120ms ease',
  '&:hover, &:focus-visible': {
    backgroundColor: colorTokens.global.brandMuted[50],
    borderColor: colorTokens.global.brandMuted[200]
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${colorTokens.global.primary[200]}`
  }
}

const popoverPaperSx: SxProps<Theme> = {
  padding: '12px 14px',
  borderRadius: '8px',
  border: `1px solid ${colorTokens.global.neutral[100]}`,
  boxShadow:
    '0 8px 24px rgba(29, 27, 32, 0.12), 0 2px 8px rgba(29, 27, 32, 0.08)',
  backgroundColor: colorTokens.semantic.brand.base.white,
  maxWidth: '320px',
  minWidth: '180px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
}

const LegendRow = ({ item }: { readonly item: KestrixChartLegendItem }) => (
  <Box sx={rowSx} role="listitem">
    <Box sx={swatchSx(item.color)} aria-hidden="true" />
    <KestrixV2Typography variant="bodySmall" weight="regular" sx={labelSx}>
      {item.label}
    </KestrixV2Typography>
  </Box>
)

/**
 * Reusable chart legend with a `+N` overflow indicator.
 *
 * When `items.length > maxVisible`, only the first `maxVisible - 1` rows are
 * shown inline; the rest collapse into an indicator that reveals them in a
 * popover on hover or keyboard focus.
 */
export const KestrixChartLegend = ({
  items,
  maxVisible = DEFAULT_MAX_VISIBLE,
  sx,
  dataTestId
}: KestrixChartLegendProps) => {
  const popoverId = useId()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const leaveTimeoutRef = useRef<number | null>(null)

  const { visibleItems, hiddenItems } = useMemo<{
    visibleItems: readonly KestrixChartLegendItem[]
    hiddenItems: readonly KestrixChartLegendItem[]
  }>(() => {
    if (items.length <= maxVisible) {
      return { visibleItems: items, hiddenItems: [] }
    }
    const cutoff = Math.max(0, maxVisible - 1)
    return {
      visibleItems: items.slice(0, cutoff),
      hiddenItems: items.slice(cutoff)
    }
  }, [items, maxVisible])

  const clearLeaveTimeout = useCallback((): void => {
    if (leaveTimeoutRef.current !== null) {
      window.clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
  }, [])

  const openPopover = useCallback(
    (target: HTMLElement): void => {
      clearLeaveTimeout()
      setAnchorEl(target)
    },
    [clearLeaveTimeout]
  )

  const scheduleClose = useCallback((): void => {
    clearLeaveTimeout()
    leaveTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(null)
      leaveTimeoutRef.current = null
    }, HOVER_LEAVE_DELAY_MS)
  }, [clearLeaveTimeout])

  const isPopoverOpen = anchorEl !== null && hiddenItems.length > 0

  return (
    <Box
      role="list"
      aria-label="Chart legend"
      data-testid={dataTestId}
      sx={{ ...containerSx, ...sx } as SxProps<Theme>}
    >
      {visibleItems.map((item) => (
        <Fragment key={item.id}>
          <LegendRow item={item} />
        </Fragment>
      ))}

      {hiddenItems.length > 0 && (
        <Box
          component="button"
          type="button"
          aria-describedby={isPopoverOpen ? popoverId : undefined}
          aria-expanded={isPopoverOpen}
          data-testid={
            dataTestId ? `${dataTestId}-overflow` : 'chart-legend-overflow'
          }
          onMouseEnter={(event) => openPopover(event.currentTarget)}
          onMouseLeave={scheduleClose}
          onFocus={(event) => openPopover(event.currentTarget)}
          onBlur={scheduleClose}
          sx={indicatorSx}
        >
          <KestrixV2Typography variant="bodySmall" weight="medium">
            +{hiddenItems.length}
          </KestrixV2Typography>
        </Box>
      )}

      <Popper
        id={popoverId}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 8, boundary: 'viewport' } },
          { name: 'flip', options: { fallbackPlacements: ['bottom', 'right'] } }
        ]}
        sx={{ zIndex: 1300 }}
        onMouseEnter={clearLeaveTimeout}
        onMouseLeave={scheduleClose}
      >
        <Paper elevation={0} sx={popoverPaperSx}>
          {hiddenItems.map((item) => (
            <Fragment key={item.id}>
              <LegendRow item={item} />
            </Fragment>
          ))}
        </Paper>
      </Popper>
    </Box>
  )
}
